// teamsFormatter.js - Enhanced version with more Teams features
import DOMPurify from 'dompurify';
 
/**
 * Converts Quill HTML to Teams-compatible HTML with FULL formatting support
 * Including: headers, colors, indentation, lists, links, etc.
 */
export const convertQuillToTeamsHTML = (htmlContent) => {
  if (!htmlContent) return '';
 
  let sanitized = DOMPurify.sanitize(htmlContent, {
    USE_PROFILES: { html: true },
    ALLOWED_TAGS: [
      'p', 'div', 'br', 'span',
      'strong', 'b', 'em', 'i', 'u', 's', 'strike',
      'ul', 'ol', 'li',
      'a', 'img',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'blockquote', 'pre', 'code',
      'font' // For color support
    ],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'style', 'color']
  });
 
  let teamsHTML = sanitized;
 
  // ENHANCED: Handle Quill's indent classes for deeply nested lists
  teamsHTML = teamsHTML.replace(
    /<li class="ql-indent-(\d+)">/gi,
    (match, level) => {
      const indentLevel = parseInt(level);
      // Use margin-left for proper indentation in Teams
      return `<li style="margin-left: ${indentLevel * 30}px;">`;
    }
  );
 
  // Handle Quill color classes (if using color picker)
  teamsHTML = teamsHTML.replace(
    /<span class="ql-color-([^"]+)">/gi,
    (match, color) => `<span style="color: ${color};">`
  );
 
  // Handle Quill background color
  teamsHTML = teamsHTML.replace(
    /<span class="ql-bg-([^"]+)">/gi,
    (match, color) => `<span style="background-color: ${color};">`
  );
 
  // Clean up whitespace in lists
  teamsHTML = teamsHTML.replace(/<ul>\s+/g, '<ul>');
  teamsHTML = teamsHTML.replace(/<ol>\s+/g, '<ol>');
  teamsHTML = teamsHTML.replace(/\s+<\/ul>/g, '</ul>');
  teamsHTML = teamsHTML.replace(/\s+<\/ol>/g, '</ol>');
 
  // Handle empty paragraphs
  teamsHTML = teamsHTML.replace(/<p>\s*(&nbsp;)?\s*<\/p>/gi, '<br/>');
 
  // Prevent excessive line breaks
  teamsHTML = teamsHTML.replace(/(<br\s*\/?>){3,}/gi, '<br/><br/>');
 
  // Normalize spaces
  teamsHTML = teamsHTML.replace(/&nbsp;|\u00A0/g, ' ');
 
  return teamsHTML.trim();
};
 
/**
 * Extracts plain text from HTML (for notifications/fallback)
 */
export const extractPlainText = (htmlContent) => {
  if (!htmlContent) return '';
 
  let text = htmlContent;
 
  // Preserve links with their URLs
  text = text.replace(
    /<a\s+[^>]*href="([^"]+)"[^>]*>(.*?)<\/a>/gi,
    (match, href, innerText) => `${innerText} (${href})`
  );
 
  // Remove style and script tags
  text = text.replace(/<style[^>]*>.*?<\/style>/gis, '');
  text = text.replace(/<script[^>]*>.*?<\/script>/gis, '');
 
  // Convert block elements to newlines
  text = text.replace(/<br\s*\/?>/gi, '\n');
  text = text.replace(/<\/p>/gi, '\n');
  text = text.replace(/<\/div>/gi, '\n');
  text = text.replace(/<\/h[1-6]>/gi, '\n');
  text = text.replace(/<\/li>/gi, '\n');
 
  // Remove all remaining HTML tags
  text = text.replace(/<[^>]+>/g, '');
 
  // Decode HTML entities
  text = text
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
 
  // Clean up excessive newlines
  text = text.replace(/\n{3,}/g, '\n\n').trim();
 
  return text;
};
 
/**
 * NEW: Converts Quill HTML to simple text/Markdown for Adaptive Cards
 * Adaptive Cards have very limited formatting support
 */
export const convertQuillToAdaptiveCardText = (htmlContent) => {
  if (!htmlContent) return '';
 
  let text = htmlContent;
 
  // Convert formatting to Markdown (Adaptive Cards support)
  text = text.replace(/<strong>(.*?)<\/strong>/gi, '**$1**');
  text = text.replace(/<b>(.*?)<\/b>/gi, '**$1**');
  text = text.replace(/<em>(.*?)<\/em>/gi, '_$1_');
  text = text.replace(/<i>(.*?)<\/i>/gi, '_$1_');
 
  // Handle links - convert to Markdown format
  text = text.replace(/<a\s+[^>]*href="([^"]+)"[^>]*>(.*?)<\/a>/gi, '[$2]($1)');
 
  // Handle lists - Adaptive Cards support simple bullet lists
  text = text.replace(/<li class="ql-indent-(\d+)">/gi, (_, level) => {
    const indent = '  '.repeat(parseInt(level)); // 2 spaces per indent level
    return '\n' + indent + '- ';
  });
  text = text.replace(/<li>/gi, '\n- ');
  text = text.replace(/<\/li>/gi, '');
  text = text.replace(/<\/?ul>/gi, '');
  text = text.replace(/<\/?ol>/gi, '');
 
  // Handle line breaks and paragraphs
  text = text.replace(/<br\s*\/?>/gi, '\n');
  text = text.replace(/<\/p>\s*<p>/gi, '\n\n');
  text = text.replace(/<\/p>/gi, '\n');
  text = text.replace(/<p>/gi, '');
  text = text.replace(/<\/div>/gi, '\n');
  text = text.replace(/<div>/gi, '');
 
  // Remove all other HTML tags
  text = text.replace(/<[^>]+>/g, '');
 
  // Decode HTML entities
  text = text
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
 
  // Clean up excessive whitespace
  text = text.replace(/ {2,}/g, ' '); // Multiple spaces to single space
  text = text.replace(/\n{3,}/g, '\n\n'); // Max 2 consecutive newlines
  text = text.trim();
 
  return text;
};
 
/**
 * Converts Quill HTML to Markdown for Adaptive Cards
 * ENHANCED: Better indentation handling for nested lists
 */
function contentExtraction(htmlContent) {  
  if (!htmlContent) {  
    return '';  
  }  
 
  const strippedHtml = htmlContent  
    .replace(/<br\s*\/?>/gi, '\n')  
    .replace(/<\/p>\s*<p>/gi, '\n\n')  
    .replace(/<\/div>\s*<div>/gi, '\n\n')
    // ENHANCED: Better indent handling - each level adds 2 spaces
    .replace(/<li class="ql-indent-(\d+)">/gi, (_, level) => {
      const indent = '  '.repeat(parseInt(level)); // 2 spaces per level
      return '\n' + indent + '- ';
    })
    .replace(/<\/li>\s*<li>/gi, '\n- ')  
    .replace(/<li>/gi, '\n- ')  
    .replace(/<\/li>/gi, '')
    .replace(/<strong>(.*?)<\/strong>/gi, '**$1**')  
    .replace(/<b>(.*?)<\/b>/gi, '**$1**')
    .replace(/<em>(.*?)<\/em>/gi, '_$1_')  
    .replace(/<i>(.*?)<\/i>/gi, '_$1_')
    .replace(/<u>(.*?)<\/u>/gi, '$1')  
    .replace(/<a href="([^"]*)">(.*?)<\/a>/gi, '[$2]($1)')  
    .replace(/<img[^>]*>/gi, '')  
    .replace(/<[^>]*>/g, '');  
 
  let resultText = strippedHtml
    .replace(/\r\n/g, '\n')  
    .replace(/\n{3,}/g, '\n\n')
    .trim();
 
  return resultText;  
}
 
/**
 * Converts Quill HTML to Adaptive Card JSON
 */
export const convertQuillToAdaptiveCard = (htmlContent, title = '') => {
  if (!htmlContent) return null;
 
  const markdown = contentExtraction(htmlContent);
  if (!markdown) return null;
 
  const card = {
    type: 'AdaptiveCard',
    $schema: 'http://adaptivecards.io/schemas/adaptive-card.json',
    version: '1.5',
    body: []
  };
 
  if (title) {
    card.body.push({
      type: 'TextBlock',
      text: title,
      weight: 'Bolder',
      size: 'Large',
      wrap: true,
      separator: true
    });
  }
 
  // Single TextBlock with full markdown content preserves ALL spacing
  card.body.push({
    type: 'TextBlock',
    text: markdown,
    wrap: true
  });
 
  return card;
};
 
/**
 * NEW: Add @mentions to the message
 * @param {Array} mentions - Array of {id: 'email', name: 'Display Name'}
 */
export const addMentionsToPayload = (mentions) => {
  if (!mentions || mentions.length === 0) return null;
 
  return mentions.map(mention => ({
    type: 'mention',
    mentioned: {
      id: mention.id,
      name: mention.name
    },
    text: `<at>${mention.name}</at>`
  }));
};
 
/**
 * NEW: Create action buttons for the message
 * @param {Array} actions - Array of {title: 'Click me', url: 'https://...'}
 */
export const createActionButtons = (actions) => {
  if (!actions || actions.length === 0) return null;
 
  return actions.map(action => ({
    type: 'Action.OpenUrl',
    title: action.title,
    url: action.url
  }));
};
 
/**
 * ENHANCED: Creates the complete Teams Send Activity payload with NEW features
 */
export const createTeamsPayload = (
  subject,
  htmlContent,
  sendTo,
  attachments = [],
  optionalEditors = [],
  options = {}
) => {
  const teamsHTML = convertQuillToTeamsHTML(htmlContent);
  const plainText = extractPlainText(htmlContent);
 
  const [popup, link] = optionalEditors;
 
  const popupTitle = popup?.popup_title || '';
  const popupContentMarkdown = (popup?.popup_content)
    ? contentExtraction(popup.popup_content)
    : '';
 
  const linkTitle = link?.Link_title || '';
  const linkContent = link?.Link_content || '';
 
  // Base payload
  const payload = {
    sendTo: (typeof sendTo === 'string' ? sendTo : '')
      .split(';')
      .map(email => email.trim())
      .filter(Boolean),
    subject,
    plainText,
    content: teamsHTML,
    // attachments: attachments || [],
    timestamp: new Date().toISOString(),
    // OptionalEditors: [
    //   {
    //     popup_title: popupTitle,
    //     popup_content: popupContentMarkdown
    //   },
    //   {
    //     Link_title: linkTitle,
    //     Link_content: linkContent
    //   }
    // ]
  };
 
  // NEW: Add mentions if provided
  if (options.mentions && options.mentions.length > 0) {
    payload.mentions = addMentionsToPayload(options.mentions);
  }
 
  // NEW: Add action buttons if provided
  if (options.actions && options.actions.length > 0) {
    payload.actions = createActionButtons(options.actions);
  }
 
  // NEW: Add notification type
  if (options.notificationType) {
    payload.notificationType = options.notificationType; // 'alert', 'reminder', 'news', 'collaboration'
  }
 
  return payload;
};
 
/**
 * Creates Teams payload WITH Adaptive Card attachment
 */
export const createTeamsPayloadWithAdaptiveCard = (
  subject,
  htmlContent,
  sendTo,
  attachments = [],
  optionalEditors = [],
  options = {}
) => {
  const plainText = extractPlainText(htmlContent);
  const [popup, link] = optionalEditors;
 
  const popupTitle = popup?.popup_title || '';
  const popupCard = (popupTitle && popup?.popup_content)
    ? convertQuillToAdaptiveCard(popup.popup_content, popupTitle)
    : null;
 
  const linkTitle = link?.Link_title || '';
  const linkContent = link?.Link_content || '';
 
  const adaptiveCardAttachments = [];
 
  // Add popup as Adaptive Card if available
  if (popupCard) {
    // Add action buttons to the card if provided
    if (options.actions && options.actions.length > 0) {
      popupCard.actions = createActionButtons(options.actions);
    }
 
    adaptiveCardAttachments.push({
      contentType: 'application/vnd.microsoft.card.adaptive',
      content: popupCard
    });
  }
 
  const payload = {
    sendTo: (typeof sendTo === 'string' ? sendTo : '')
      .split(';')
      .map(email => email.trim())
      .filter(Boolean),
    subject,
    plainText,
    // attachments: [...adaptiveCardAttachments, ...(attachments || [])],
    timestamp: new Date().toISOString(),
    // OptionalEditors: [
    //   {
    //     popup_title: popupTitle,
    //     popup_content: popupCard ? JSON.stringify(popupCard) : ''
    //   },
    //   {
    //     Link_title: linkTitle,
    //     Link_content: linkContent
    //   }
    // ]
  };
 
  // Add mentions if provided
  if (options.mentions && options.mentions.length > 0) {
    payload.mentions = addMentionsToPayload(options.mentions);
  }
 
  if (options.notificationType) {
    payload.notificationType = options.notificationType;
  }
 
  return payload;
};
 
/**
 * Create individual activity messages
 */
export const createActivityMessage = (content, attachments = [], options = {}) => {
  const teamsHTML = convertQuillToTeamsHTML(content);
  const plainText = extractPlainText(content);
 
  const activity = {
    type: 'message',
    text: plainText,
    textFormat: 'xml',
    channelData: {
      text: teamsHTML
    },
    importance: options.importance || 'normal'
  };
 
  if (attachments?.length > 0) {
    activity.attachments = attachments.map(attachment => ({
      contentType: 'image/png',
      contentUrl: attachment,
      name: 'attachment'
    }));
  }
 
  // Add mentions if provided
  if (options.mentions && options.mentions.length > 0) {
    activity.entities = addMentionsToPayload(options.mentions);
  }
 
  return activity;
};
 
/**
 * Create activity message with Adaptive Card
 */
export const createActivityMessageWithAdaptiveCard = (title, content, options = {}) => {
  const adaptiveCard = convertQuillToAdaptiveCard(content, title);
  const plainText = extractPlainText(content);
 
  if (!adaptiveCard) {
    return createActivityMessage(content, [], options);
  }
 
  // Add action buttons to the card if provided
  if (options.actions && options.actions.length > 0) {
    adaptiveCard.actions = createActionButtons(options.actions);
  }
 
  return {
    type: 'message',
    text: plainText,
    importance: options.importance || 'normal',
    attachments: [
      {
        contentType: 'application/vnd.microsoft.card.adaptive',
        content: adaptiveCard
      }
    ]
  };
};
 
/**
 * NEW: Helper to parse mentions from text like "@John Doe" or "@john@example.com"
 * This can be used to extract mentions from the message content
 */
export const extractMentionsFromText = (text, userDatabase = []) => {
  const mentionRegex = /@(\S+@\S+\.\S+)|@([A-Z][a-z]+\s[A-Z][a-z]+)/g;
  const mentions = [];
  let match;
 
  while ((match = mentionRegex.exec(text)) !== null) {
    const mentionText = match[1] || match[2];
   
    // Try to find user in database
    const user = userDatabase.find(u =>
      u.email === mentionText || u.name === mentionText
    );
 
    if (user) {
      mentions.push({
        id: user.email,
        name: user.name || user.email
      });
    }
  }
 
  return mentions;
};
 